// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import { remoteGet, remotePost } from '../utils/remoteApi';
import { createCancelableRemoteGet } from '../utils/remoteApiCancelable';
import axios from 'axios';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ContentSkeleton from './ContentSkeleton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { green } from '@mui/material/colors';
import { useTheme } from '../context/ThemeContext';
import { getGlobalBackoff } from '../utils/exponentialBackoff';

// Helper for color based on percentage
const getStatusColor = (percent) => {
  if (percent === null || percent === undefined) return '#43a047'; // default green
  if (percent >= 60) return '#e53935'; // red
  if (percent >= 25) return '#ff9800'; // yellow
  return '#43a047'; // green
};

const StargateSystemInfo = ({ ip, apiKey, showLoadingOverlay }) => {
  const { isDarkMode } = useTheme();
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [cpuHistory, setCpuHistory] = useState([]); // For sparkline
  const cpuHistoryRef = useRef([]);

  const intervalRef = useRef(null);
  const activeRequestsRef = useRef([]);
  const backoff = getGlobalBackoff();

  // Base polling interval (will be adjusted by backoff on errors)
  const BASE_POLLING_INTERVAL = 5000; // 5 seconds

  useEffect(() => {
    const fetchAndPoll = async () => {
      await fetchSystemInfo(true); // Initial load

      // Start polling with exponential backoff on errors
      const scheduleNextPoll = () => {
        if (intervalRef.current) clearTimeout(intervalRef.current);

        // Get backoff delay for system info polling
        const backoffKey = `system-info-${ip}`;
        const delay = backoff.getDelay(backoffKey) || BASE_POLLING_INTERVAL;

        intervalRef.current = setTimeout(() => {
          fetchSystemInfo(false).finally(() => {
            scheduleNextPoll(); // Schedule next poll regardless of success/failure
          });
        }, delay);
      };

      scheduleNextPoll();
    };

    fetchAndPoll();
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      // Cancel all active requests when component unmounts or IP/apiKey changes
      activeRequestsRef.current.forEach((req) => {
        if (req && req.cancel) {
          req.cancel();
        }
      });
      activeRequestsRef.current = [];
      // Reset backoff state for this component
      backoff.resetKey(`system-info-${ip}`);
    };
  }, [ip, apiKey]);

  // Fetch system info from multiple endpoints and merge results
  const fetchSystemInfo = async (isInitial = false) => {
    if (isInitial || !hasLoadedOnce) setLoading(true);

    // Clear previous requests
    activeRequestsRef.current.forEach((req) => {
      if (req && req.cancel) {
        req.cancel();
      }
    });
    activeRequestsRef.current = [];

    try {
      // Create cancelable requests
      const infoReq = createCancelableRemoteGet(ip, apiKey, '/api/system/info');
      const cpuReq = createCancelableRemoteGet(ip, apiKey, '/api/system/status/cpu');
      const memoryReq = createCancelableRemoteGet(ip, apiKey, '/api/system/status/memory');
      const diskReq = createCancelableRemoteGet(ip, apiKey, '/api/system/status/disk');

      // Store requests for cancellation
      activeRequestsRef.current = [infoReq, cpuReq, memoryReq, diskReq];

      // Fetch basic system info with individual error handling
      // Use Promise.allSettled to handle partial failures gracefully and prevent unhandled rejections
      const results = await Promise.allSettled([
        infoReq.request,
        cpuReq.request,
        memoryReq.request,
        diskReq.request,
      ]);

      // Extract results, handling failures gracefully
      const info = results[0].status === 'fulfilled' ? results[0].value : null;
      const cpu = results[1].status === 'fulfilled' ? results[1].value : null;
      const memory = results[2].status === 'fulfilled' ? results[2].value : null;
      const disk = results[3].status === 'fulfilled' ? results[3].value : {};

      // Check if all requests failed - if so, throw the first error to trigger catch block
      const allFailed = results.every((r) => r.status === 'rejected');
      let firstError = null;

      // Log any failures (but don't throw if we have partial data)
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const error = result.reason;
          if (!firstError && !axios.isCancel(error)) {
            firstError = error;
          }
          // Don't log canceled requests
          if (!axios.isCancel(error)) {
            const endpointNames = ['info', 'cpu', 'memory', 'disk'];
            const isTransientError =
              error.response && [502, 503, 504].includes(error.response.status);
            if (!isTransientError) {
              console.warn(`[SystemInfo] Failed to fetch ${endpointNames[index]}:`, error.message);
            }
          }
        }
      });

      // If all requests failed, throw to trigger catch block
      if (allFailed && firstError) {
        throw firstError;
      }

      // Clear active requests after successful completion
      activeRequestsRef.current = [];
      // Find the root and /data partitions.
      //
      // On Stargate firmware '/' is an overlayfs whose upper dir lives on the
      // /data partition, so the reported '/' numbers DUPLICATE /data. The real
      // (read-only, A/B) root partition is only visible through bind mounts
      // like /version.dat or /etc/machine-id. Prefer any non-/data,
      // non-overlay entry whose size differs from /data — that's the actual
      // root partition, matching what the Stargate's own UI shows.
      const diskKeys = Object.keys(disk);
      const dataKey = diskKeys.find((k) => k === '/data' || k.startsWith('/data'));
      const dataTotal = dataKey ? disk[dataKey]?.total : undefined;
      const realRootKey = diskKeys.find(
        (k) =>
          !k.startsWith('/data') &&
          disk[k]?.fstype !== 'overlay' &&
          typeof disk[k]?.total === 'number' &&
          disk[k].total !== dataTotal
      );
      const rootKey =
        realRootKey ||
        diskKeys.find((k) => k === '/') ||
        diskKeys.find((k) => !k.startsWith('/data')) ||
        diskKeys[0];
      // Only show the /data card when it's genuinely a different filesystem
      // than whatever we ended up using for "root".
      const sameAsRoot =
        dataKey &&
        rootKey &&
        disk[dataKey]?.total === disk[rootKey]?.total &&
        disk[dataKey]?.used === disk[rootKey]?.used;
      const dataKeyResolved = dataKey && dataKey !== rootKey && !sameAsRoot ? dataKey : undefined;
      const merged = {
        ...info,
        // CPU
        cpu_percent: cpu?.cpu_percent,
        cpu_count: cpu?.cpu_count,
        loadavg: cpu?.loadavg,
        // Memory
        memory_total: memory?.total,
        memory_used: memory?.used,
        memory_percent: memory?.percent,
        memory_free: memory?.free,
        // Disk root ('/'). When the root stats come from a bind-mount proxy
        // (e.g. /version.dat), still label it as '/' — it IS the root partition.
        disk_mount: realRootKey ? '/' : rootKey || '',
        disk_total: disk[rootKey]?.total,
        disk_used: disk[rootKey]?.used,
        disk_percent: disk[rootKey]?.percent,
        disk_free: disk[rootKey]?.free,
        disk_fs: disk[rootKey]?.fstype,
        // Disk /data (optional, only if it's a distinct partition from root)
        data_disk_mount: dataKeyResolved || '',
        data_disk_total: dataKeyResolved ? disk[dataKeyResolved]?.total : undefined,
        data_disk_used: dataKeyResolved ? disk[dataKeyResolved]?.used : undefined,
        data_disk_percent: dataKeyResolved ? disk[dataKeyResolved]?.percent : undefined,
        data_disk_free: dataKeyResolved ? disk[dataKeyResolved]?.free : undefined,
        data_disk_fs: dataKeyResolved ? disk[dataKeyResolved]?.fstype : undefined,
      };

      // Remove sensitive/unwanted fields before setting systemInfo
      const filtered = Object.fromEntries(
        Object.entries(merged).filter(
          ([key]) =>
            !['region_id', 'regionId', 'ip', 'ip_address', 'api_key', 'apikey', 'apiKey'].includes(
              key.toLowerCase()
            )
        )
      );
      setSystemInfo(filtered);
      // CPU sparkline
      if (typeof cpu?.cpu_percent === 'number') {
        cpuHistoryRef.current = [...cpuHistoryRef.current, cpu.cpu_percent].slice(-30);
        setCpuHistory([...cpuHistoryRef.current]);
      }
      if (!hasLoadedOnce) setHasLoadedOnce(true);

      // Record success with backoff
      backoff.recordSuccess(`system-info-${ip}`);
    } catch (e) {
      // Don't log or show error for canceled requests
      if (axios.isCancel(e)) {
        console.log('[SystemInfo] Request canceled');
        return;
      }

      // Record failure with exponential backoff
      const nextDelay = backoff.recordFailure(`system-info-${ip}`, e);

      // Check if this is a transient error (502/503/504)
      const isTransientError = e.response && [502, 503, 504].includes(e.response.status);

      // Only show error if not throttled and not a transient error
      if (!backoff.shouldThrottleError() && !isTransientError) {
        console.warn(
          `[SystemInfo] Failed to fetch system info, next retry in ${Math.round(nextDelay / 1000)}s`
        );

        let message = 'Failed to fetch system info.';
        if (e.response && e.response.status === 401) {
          message =
            'AUTH ERROR: API Key was rejected by the remote Stargate. Please check the API key.';
        } else if (e.response) {
          message = `COMMUNICATION ERROR: Status ${e.response.status}`;
        } else if (e.request) {
          message = 'No response from device';
        } else {
          message = `Error: ${e.message}`;
        }
        setSnackbar({ open: true, message, severity: 'error' });
      } else if (isTransientError && !backoff.shouldThrottleError()) {
        // For transient errors, just log a warning without showing to user
        console.warn(
          `[SystemInfo] Transient error (${e.response.status}), will retry in ${Math.round(nextDelay / 1000)}s`
        );
      }
    } finally {
      if (!hasLoadedOnce) setLoading(false);
    }
  };

  const handleReboot = async () => {
    try {
      await remotePost(ip, apiKey, '/api/system/reboot', {});
      setSnackbar({
        open: true,
        message: 'Reboot command sent',
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: 'Failed to send reboot command',
        severity: 'error',
      });
    }
  };

  // Helpers
  const formatBytes = (bytes) => {
    if (!bytes || typeof bytes !== 'number' || !isFinite(bytes) || bytes <= 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  const formatUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  };

  // Render
  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 20 }}>
            System Status
          </Typography>
        }
        action={
          <Button
            onClick={fetchSystemInfo}
            size="small"
            sx={{
              minWidth: 0,
              color: '#fff',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 2,
              '&:hover': { background: 'rgba(255,255,255,0.18)' },
            }}
            disabled={loading}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <svg
                style={{ width: 22, height: 22 }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 5.36A9 9 0 0020.49 15"></path>
              </svg>
            </span>
          </Button>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
          mb: 2,
        }}
      />
      <CardContent sx={{ position: 'relative' }}>
        {/* Skeleton loading state */}
        {loading && !hasLoadedOnce && <ContentSkeleton variant="metric" rows={6} columns={3} />}
        {systemInfo && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* CPU Usage */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="subtitle2">CPU Usage</Typography>
                  <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
                    {(() => {
                      const cpu =
                        typeof systemInfo.cpu_percent === 'number' ? systemInfo.cpu_percent : null;
                      let color = '#43a047',
                        label = 'Excellent';
                      if (cpu !== null) {
                        if (cpu >= 60) {
                          color = '#e53935';
                          label = 'High';
                        } else if (cpu >= 25) {
                          color = '#ff9800';
                          label = 'Moderate';
                        } else {
                          color = '#43a047';
                          label = 'Excellent';
                        }
                      }
                      return (
                        <>
                          <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
                            {cpu !== null ? `${cpu.toFixed(1)}%` : '—'}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mt: 1,
                            }}
                          >
                            <Chip
                              label={label}
                              size="small"
                              sx={{
                                background: color,
                                color: 'text.primary',
                                fontWeight: 700,
                              }}
                            />
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Card>
              </Grid>
              {/* Memory Usage */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="subtitle2">Memory Usage</Typography>
                  {(() => {
                    const mem =
                      typeof systemInfo.memory_percent === 'number'
                        ? systemInfo.memory_percent
                        : null;
                    let color = '#43a047',
                      label = 'Excellent';
                    if (mem !== null) {
                      if (mem >= 60) {
                        color = '#e53935';
                        label = 'High';
                      } else if (mem >= 25) {
                        color = '#ff9800';
                        label = 'Moderate';
                      } else {
                        color = '#43a047';
                        label = 'Excellent';
                      }
                    }
                    return (
                      <>
                        <Typography variant="h5" sx={{ color, fontWeight: 700 }}>
                          {mem !== null ? `${mem}%` : '—'}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mt: 1,
                          }}
                        >
                          <Chip
                            label={label}
                            size="small"
                            sx={{
                              background: color,
                              color: 'text.primary',
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={mem !== null ? mem : 0}
                          sx={{
                            height: 8,
                            borderRadius: 2,
                            mt: 1,
                            mb: 1,
                            background: isDarkMode
                              ? 'rgba(138, 148, 168, 0.1)'
                              : 'rgba(0,0,0,0.08)',
                            '& .MuiLinearProgress-bar': { background: color },
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatBytes(systemInfo.memory_used)} /{' '}
                          {formatBytes(systemInfo.memory_total)}
                        </Typography>
                      </>
                    );
                  })()}
                </Card>
              </Grid>
              {/* Uptime */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="subtitle2">System Uptime</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
                      {formatUptime(systemInfo.uptime || 0)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {systemInfo.hostname} • {systemInfo.kernel}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            {/* Disk Usage - Root Partition */}
            <Card
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Typography variant="subtitle2">Disk Usage (Root)</Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Mount: {systemInfo.disk_mount || '/'}
              </Typography>
              {(() => {
                const disk =
                  typeof systemInfo.disk_percent === 'number' ? systemInfo.disk_percent : null;
                const color = getStatusColor(disk);
                return (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={disk !== null ? disk : 0}
                      sx={{
                        height: 8,
                        borderRadius: 2,
                        mt: 1,
                        mb: 1,
                        background: isDarkMode ? 'rgba(138, 148, 168, 0.1)' : 'rgba(0,0,0,0.08)',
                        '& .MuiLinearProgress-bar': { background: color },
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatBytes(systemInfo.disk_used)} used of{' '}
                        {formatBytes(systemInfo.disk_total)}
                      </Typography>
                      <Chip
                        label={`${disk !== null ? disk : 0}%`}
                        size="small"
                        sx={{
                          background: color,
                          color: 'text.primary',
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        FS: {systemInfo.disk_fs || 'ext4'}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Card>

            {/* Disk Usage - /data Partition */}
            {systemInfo.data_disk_mount && (
              <Card
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2,
                  background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle2">Disk Usage (/data)</Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  Mount: {systemInfo.data_disk_mount}
                </Typography>
                {(() => {
                  const disk =
                    typeof systemInfo.data_disk_percent === 'number'
                      ? systemInfo.data_disk_percent
                      : null;
                  const color = getStatusColor(disk);
                  return (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={disk !== null ? disk : 0}
                        sx={{
                          height: 8,
                          borderRadius: 2,
                          mt: 1,
                          mb: 1,
                          background: isDarkMode ? 'rgba(138, 148, 168, 0.1)' : 'rgba(0,0,0,0.08)',
                          '& .MuiLinearProgress-bar': { background: color },
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatBytes(systemInfo.data_disk_used)} used of{' '}
                          {formatBytes(systemInfo.data_disk_total)}
                        </Typography>
                        <Chip
                          label={`${disk !== null ? disk : 0}%`}
                          size="small"
                          sx={{
                            background: color,
                            color: 'text.primary',
                            fontWeight: 700,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          FS: {systemInfo.data_disk_fs || 'ext4'}
                        </Typography>
                      </Box>
                    </>
                  );
                })()}
              </Card>
            )}

            {/* Health Status */}
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ color: green[400], fontSize: 32 }} />
              <Typography variant="h6" sx={{ color: green[400], fontWeight: 700 }}>
                System is Healthy
              </Typography>
              <Chip label="HEALTHY" color="success" size="medium" sx={{ fontWeight: 700, ml: 2 }} />
            </Box>
          </Box>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default StargateSystemInfo;
